import type { Lead, NotesEntry } from "../components/lead-card";

export type NoteMutation =
  | { type: 'add'; note: NotesEntry }
  | { type: 'edit'; noteId: string; content: string; expectedContent: string }
  | { type: 'delete'; noteId: string; expectedContent: string };

// Both the UI and transaction need the same identity for old single-text notes.
export function getLeadNotes(lead: Pick<Lead, 'id'> & Partial<Pick<Lead, 'notes' | 'notesHistory' | 'createdAt' | 'assignedTo'>>): NotesEntry[] {
  if (lead.notesHistory?.length) return lead.notesHistory;
  if (!lead.notes?.trim()) return [];
  const createdAt = lead.createdAt ? new Date(lead.createdAt) : new Date(0);
  return [{
    id: `migrated-${lead.id}`,
    content: lead.notes.trim(),
    timestamp: Number.isNaN(createdAt.getTime()) ? new Date(0).toISOString() : createdAt.toISOString(),
    createdBy: lead.assignedTo || 'System',
  }];
}

export function applyNoteMutation(notes: NotesEntry[], mutation: NoteMutation): NotesEntry[] {
  if (mutation.type === 'add') {
    const existing = notes.find(note => note.id === mutation.note.id);
    if (existing) {
      if (existing.content === mutation.note.content && existing.timestamp === mutation.note.timestamp && existing.createdBy === mutation.note.createdBy) return notes;
      throw new Error('Notiz wurde zwischenzeitlich geändert. Bitte neu laden.');
    }
    return [mutation.note, ...notes];
  }
  const existing = notes.find(note => note.id === mutation.noteId);
  if (!existing || existing.content !== mutation.expectedContent) {
    throw new Error('Notiz wurde zwischenzeitlich geändert oder gelöscht. Bitte neu laden.');
  }
  return mutation.type === 'delete'
    ? notes.filter(note => note.id !== mutation.noteId)
    : notes.map(note => note.id === mutation.noteId ? { ...note, content: mutation.content } : note);
}

// General form saves may append automatic notes, but must never replace the
// separately managed note list with a stale form snapshot.
export function getLeadAppends(update: Partial<Lead>, original: Lead) {
  return {
    notes: (update.notesHistory || []).filter(note =>
      !(original.notesHistory || []).some(existing => existing.id === note.id)),
    history: (update.history || []).filter(entry =>
      !(original.history || []).some(existing => JSON.stringify(existing) === JSON.stringify(entry))),
  };
}
